import { useRefresh } from 'react-admin';
import CheckIcon from '@mui/icons-material/Check';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { adminRpcClient } from '@/providers/backendClient';

type ForceAggregationProps = {
  sessionNote: any;
  recordings: any[];
};

export const ForceAggregationButton = ({
  sessionNote,
  recordings,
}: ForceAggregationProps) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const aggregateAvailable = useMemo(
    () => recordings?.some(rec => rec.status === 'transcribed'),
    [recordings],
  );

  const refresh = useRefresh();

  const handleOpenDialog = useCallback(() => {
    setConfirmDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setConfirmDialogOpen(false);
  }, []);

  const onForceAggregation = useCallback(async () => {
    setIsUpdating(true);
    setConfirmDialogOpen(false);

    await adminRpcClient('PUBLIC_ADMIN_therapistAsset_aggregate', [
      sessionNote.id,
      'therapistSessionNotes',
    ]).catch(err => {
      alert(
        'Error aggregating note inputs: ' + err.message,
      );
    });

    setIsUpdating(false);

    refresh();
  }, [refresh, sessionNote.id]);

  return aggregateAvailable ? (
    <>
      <Button
        disabled={isUpdating}
        onClick={handleOpenDialog}
        startIcon={
          !isUpdating ? (
            <CheckIcon />
          ) : (
            <CircularProgress size="1rem" />
          )
        }
      >
        {!isUpdating ? 'Force Aggregation' : 'Aggregating'}
      </Button>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="force-aggregation-dialog-title"
        aria-describedby="force-aggregation-dialog-description"
      >
        <DialogTitle id="force-aggregation-dialog-title">
          Confirm force aggregation
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="force-aggregation-dialog-description">
            Are you sure you want to force aggregation for this session
            note? This re-runs aggregation from transcribed recordings and
            may replace existing aggregated content.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={onForceAggregation}
            color="primary"
            variant="contained"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  ) : null;
};
